import React, {memo, FC, useState, useEffect, CSSProperties, useCallback} from "react";
import {Link} from "@montana/shared/link";
import {useTranslations} from "@montana/containers/translations";
import {SvgIcon} from "@montana/shared/svg-icon";
import {formatString} from "@montana/helpers/string-format/string-format.helper";
import {Translate} from "@montana/shared/translate";
import {IOpeningHours} from "@montana/models/generated/cms.interface";
import {IEnhancedStore} from "@montana/models/dev";
import {TrackingTypes} from "@montana/helpers/tracking.helper";
import {formatUrlToGoogleMaps} from '@montana/helpers/google-maps.helper';

interface IStoreListItemProps {
    store: IEnhancedStore;
    show: boolean;
    selected: boolean;
    withHorizontalPadding?: boolean;
    independent?: boolean;
    style?: CSSProperties;
    onSelect: () => void;
    onTimeTableToggle?: (isOpen: boolean) => void;
}

export const StoreListItem: FC<IStoreListItemProps> = memo(({
                                                                store,
                                                                onSelect,
                                                                selected,
                                                                independent = false,
                                                                style,
                                                                show,
                                                                onTimeTableToggle
                                                            }: IStoreListItemProps) => {
    const [openingHoursHeader, setOpeningHoursHeader] = useState<string>();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [showTimeTable, setShowTimeTable] = useState<boolean>(false);

    const translate = useTranslations();

    useEffect(() => {
        !!onTimeTableToggle && onTimeTableToggle(showTimeTable);
    }, [showTimeTable])

    const zeroPadTimeString = (time?: number) => time ? `${time < 10 ? '0' : ''}${time}` : '00';

    const getTimetableRow = useCallback(
        (row: IOpeningHours) => {
            let dayOfTheWeek;

            switch (row.dayOfWeek) {
                case "monday":
                    dayOfTheWeek = translate(t => t.general.monday, "Monday");
                    break;
                case "tuesday":
                    dayOfTheWeek = translate(t => t.general.tuesday, "Tuesday");
                    break;
                case "wednesday":
                    dayOfTheWeek = translate(t => t.general.wednesday, "Wednesday");
                    break;
                case "thursday":
                    dayOfTheWeek = translate(t => t.general.thursday, "Thursday");
                    break;
                case "friday":
                    dayOfTheWeek = translate(t => t.general.friday, "Friday");
                    break;
                case "saturday":
                    dayOfTheWeek = translate(t => t.general.saturday, "Saturday");
                    break;
                case "sunday":
                    dayOfTheWeek = translate(t => t.general.sunday, "Sunday");
                    break;

                default:
                    break;
            }

            const closed = translate(t => t.storeLocator.storeClosed, "Closed");
            const time = !row || row?.closed
                ? closed
                : `${row.openingHour}:${zeroPadTimeString(row.openingMinute)}-${row.closingHour}:${zeroPadTimeString(row.closingMinute)}`;

            let time2 = '';
            if (row?.openingHour2 && row?.closingHour2) {
                time2 = `, ${row.openingHour2}:${zeroPadTimeString(row.openingMinute2)}-${row.closingHour2}:${zeroPadTimeString(row.closingMinute2)}`;
            }

            return (
                <>
                    <td>{`${dayOfTheWeek}: `}</td>
                    <td>{`${time}${time2}`}</td>
                </>
            );
        }, [store.shopTimeTable]
    );


    function initOpeningHours() {
        if (!store?.shopTimeTable?.length) {
            return;
        }

        let header: string;

        const currentDate = new Date();

        //For testing specific dates and times
        //const currentDate = new Date(Date.parse('09/27/2023 12:59:00'));

        // Sunday - Saturday : 0 - 6
        const currentDayOfWeek = currentDate.getDay();
        const dayIndex = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
        const dayOfWeekRow = dayIndex < store.shopTimeTable.length ? store.shopTimeTable[dayIndex] : undefined;
        const dayTomorrow = dayIndex < store.shopTimeTable.length ? store.shopTimeTable[dayIndex + 1] : undefined;

        if (!!dayOfWeekRow && !!dayOfWeekRow?.openingHour && !!dayOfWeekRow?.closingHour && dayTomorrow) {
            const openHours = new Date();
            openHours.setHours(dayOfWeekRow.openingHour, dayOfWeekRow.openingMinute, 0, 0);

            const openHours2 = new Date();
            if (dayOfWeekRow.openingHour2) {
                openHours2.setHours(dayOfWeekRow.openingHour2, dayOfWeekRow.openingMinute2, 0, 0);
            }

            const closeHours = new Date();
            closeHours.setHours(dayOfWeekRow.closingHour, dayOfWeekRow.closingMinute, 0, 0);

            const closeHours2 = new Date();
            if (dayOfWeekRow.closingHour2) {
                closeHours2.setHours(dayOfWeekRow.closingHour2, dayOfWeekRow.closingMinute2, 0, 0);
            }

            // If current time is between opening and closing hours
            if (currentDate.getTime() >= openHours.getTime() && currentDate.getTime() < closeHours.getTime()) {
                header = formatString(
                    translate(t => t.storeLocator.storeClosesAt, "Closes at {0}"),
                    `${dayOfWeekRow.closingHour}:${dayOfWeekRow.closingMinute || "00"}`
                );
                if (dayOfWeekRow.openingHour2) {
                    header += ', ';
                    header += formatString(translate(t => t.storeLocator.opensAgainAt, ", Opens again at {0}"), `${dayOfWeekRow.openingHour2}:${dayOfWeekRow.openingMinute2 || "00"}`).toLowerCase();
                }
                setIsOpen(true);
            }
            // If current time is between closing and second opening hours
            else if (currentDate.getTime() > closeHours.getTime() && currentDate.getTime() < openHours2.getTime()) {
                header = formatString(
                    translate(t => t.storeLocator.opensAgainAt, "Opens again at {0}"),
                    `${dayOfWeekRow.openingHour2}:${dayOfWeekRow.openingMinute2 || "00"}`
                );
                setIsOpen(false);
            }
            // If current time is between second opening and second closing hours
            else if (currentDate.getTime() >= openHours2.getTime() && currentDate.getTime() < closeHours2.getTime()) {
                header = formatString(
                    translate(t => t.storeLocator.storeClosesAt, "Closes at {0}"),
                    `${dayOfWeekRow.closingHour2}:${dayOfWeekRow.closingMinute2 || "00"}`
                );
                setIsOpen(true);

            }
            // If current time is before opening hours
            else if (currentDate.getTime() < openHours.getTime()) {
                header = formatString(
                    translate(t => t.storeLocator.storeOpensAt, "Opens at {0}"),
                    `${dayOfWeekRow.openingHour}:${dayOfWeekRow.openingMinute || "00"}`
                );

                setIsOpen(false);
            }
            // If current time is after closing hours
            else {
                header = formatString(
                    translate(t => t.storeLocator.storeOpensAt, "Opens at {0}"),
                    `${dayTomorrow.openingHour}:${dayTomorrow.openingMinute || "00"}`
                );

                setIsOpen(false);
            }
        } else {
            header = translate(t => t.storeLocator.storeClosedToday, "Closed today");
            setIsOpen(false);
        }

        setOpeningHoursHeader(header);
    }

    useEffect(() => {
        initOpeningHours();
    }, []);

    return (
        <article
            className={`store-item${selected ? ' store-item--selected' : ''}${!show ? ' store-item--hidden' : ''}${independent ? ' store-item--independent' : ''}`}
            style={style}
            role="button"
            onClick={() => !independent && onSelect()}
            key={store.id}
        >
            <div>
                <div className="store-item__type">
                    <Translate
                        value={store.physicalStore ? "storeLocator.shopType" : "storeLocator.webshopType"}
                        defaultValue={store.physicalStore ? "Shop" : "Webshop"}
                    />
                </div>
                <h5 className="store-item__name">{store.name}</h5>
                <div className="store-item__address">
                    <div>
                        <div>{store.address}</div>
                        <div>{store.zipCode} {store.city}</div>
                        <div>{store.country}</div>
                    </div>
                    <div>
                        {!!store.distance && (
                            <div className="store-item__distance">
                                <Translate value="storeLocator.storeApproxDistance"
                                           defaultValue="Distance approx. {0} km.">
                                    {t => formatString(t, `${store.distance?.toFixed(store.distance > 999 ? 0 : 1) || ''}`)}
                                </Translate>
                            </div>
                        )}
                    </div>
                </div>
                <div className="store-item__link-to-google-maps">
                    <Link
                        withStroke
                        to={formatUrlToGoogleMaps(store)}
                        trackingType={TrackingTypes.OutgoingLink}
                        trackingPayload={{
                            event: 'dealerGoToGoogleMaps',
                            city: store?.city || '',
                            postalCode: store?.zipCode || '',
                            country: store?.country || '',
                            phone: store?.phoneNumber || '',
                        }}
                    >
                        <Translate value={(t): string => t.storeLocator.openInGoogleMaps}
                                   defaultValue="[Open in Google Maps]"/>
                    </Link>
                </div>
                <div className="store-item__contact">
                    {!!store.websiteUrl && (
                        <Link
                            className="store-item__website"
                            to={store.websiteUrl}
                            withStroke
                            trackingType={TrackingTypes.Other}
                            trackingPayload={{
                                event: 'dealerClick',
                                city: store.city || '',
                                postalCode: store.zipCode || '',
                                country: store.country || '',
                                phone: store.phoneNumber || '',
                            }}
                        >
                            {store.websiteText ?? store.websiteUrl}
                        </Link>
                    )}
                    {!!store.email && (
                        <Link
                            className="store-item__email"
                            to={`mailto:${store.email}`}
                            withStroke
                            trackingType={TrackingTypes.Other}
                            trackingPayload={{
                                event: 'dealerClick',
                                city: store.city || '',
                                postalCode: store.zipCode || '',
                                country: store.country || '',
                                phone: store.phoneNumber || '',
                            }}
                        >
                            {store.email}
                        </Link>
                    )}
                    {!!store.phoneNumber && (
                        <Link
                            className="store-item__email"
                            to={`tel:${store.phoneNumber}`}
                            withStroke
                            trackingType={TrackingTypes.Other}
                            trackingPayload={{
                                event: 'dealerClick',
                                city: store.city || '',
                                postalCode: store.zipCode || '',
                                country: store.country || '',
                                phone: store.phoneNumber || '',
                            }}
                        >
                            {store.phoneNumber}
                        </Link>
                    )}
                </div>
                {openingHoursHeader && (
                    <div className="store-item-hours">
                        <span
                            className={`store-item-hours__status${!isOpen ? ' store-item-hours__status--closed' : ''}`}>
                            {isOpen ? (translate(t => t.storeLocator.storeOpen, "Open")) : (translate(t => t.storeLocator.storeClosed, "Closed"))}:&nbsp;
                        </span>
                        <div className="store-item-hours__wrapper">
                            <button type="button" title="Toggle show time table" className="store-item-hours__header"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowTimeTable(!showTimeTable);
                                    }}>
                                <span className="store-item-hours__info">
                                    {openingHoursHeader}
                                </span>
                                <SvgIcon
                                    className="store-item-hours__chevron"
                                    iconName="chevron_down"
                                />
                            </button>
                            {showTimeTable && (
                                <table className="store-item-hours__table">
                                    <tbody>
                                    {!!store?.shopTimeTable?.length && store.shopTimeTable.map(row =>
                                        <tr
                                            key={row.dayOfWeek}
                                            className="store-item-hours__info"
                                        >
                                            {getTimetableRow(row)}
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </article>
    )
});
